import { debounce, isEqual } from 'lodash-es';
import { router } from '#src/main';
import { initialContext } from '#src/state-machine/definition';
import { type MachineContextType } from '#src/state-machine/types';
import {
  type YearsMode,
  type ChartType,
  type ViewType,
  type TableDataViewType,
} from '#src/types';

export const updateUrlDebounced = debounce((context: MachineContextType) => {
  const searchObject = contextToSearchObject(context);
  try {
    void router.navigate({
      search: (prev: any) => {
        const newSearch =
          searchObject != null ? { ...prev, ...searchObject } : {};

        return removeEmptyArrays(newSearch);
      },
      replace: true,
    } as any);
  } catch (error) {
    console.error('Failed to update URL:', error);
  }
}, 300);

export function contextToSearchObject(
  context: MachineContextType
): Partial<MachineContextType> | null {
  // Define which properties to sync
  const validProps = [
    'dataset',
    'yearsMode',
    'selectedYears',
    'level',
    'category',
    'fundingSource',
    'compareBy',
    'levelCompare',
    'categoryCompare',
    'fundingSourceCompare',
    'viewType',
    'chartType',
    'contextualDataset',
    'tableDataView',
  ] as const;

  const allDefault = validProps.every((prop) => {
    return isEqual(context[prop], initialContext[prop]);
  });

  if (allDefault) return null;

  const params: Record<string, any> = {};

  // Only include properties with truthy values
  validProps.forEach((prop) => {
    const value = context[prop];
    if (value != null) {
      params[prop] = value;
    }
  });

  return params;
}

export function parseUrlToContext(
  params: Record<string, any>
): Partial<MachineContextType> {
  // Parse selectedYears helper
  const parseSelectedYears = (param: any): number[] | undefined => {
    return param?.map((year: any) =>
      typeof year === 'string' ? parseInt(year, 10) : year
    );
  };

  // Direct assignment - let undefined values fall through
  const context = {
    dataset: params.dataset as string,
    yearsMode: params.yearsMode as YearsMode,
    selectedYears: parseSelectedYears(params.selectedYears),
    level: params.level as string,
    category: params.category as string,
    fundingSource: params.fundingSource as string,
    compareBy: params.compareBy,
    levelCompare: params.levelCompare,
    categoryCompare: params.categoryCompare,
    fundingSourceCompare: params.fundingSourceCompare,
    viewType: params.viewType as ViewType,
    chartType: params.chartType as ChartType,
    contextualDataset: params.contextualDataset as string,
    tableDataView: params.tableDataView as TableDataViewType,
  };

  return removeUndefinedValues(context);
}

function removeUndefinedValues(
  context: Partial<MachineContextType>
): Partial<MachineContextType> {
  const cleanContext: Partial<MachineContextType> = {};
  Object.entries(context).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length === 0) {
      return;
    }

    if (value != null && value !== '') {
      (cleanContext as any)[key] = value;
    }
  });

  return cleanContext;
}

function removeEmptyArrays(
  context: Partial<MachineContextType>
): Partial<MachineContextType> {
  const cleanContext: Partial<MachineContextType> = {};
  Object.entries(context).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    (cleanContext as any)[key] = value;
  });

  return cleanContext;
}
